import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { withTranslation } from '../../ChildrenContext.js';
import PropTypes from 'prop-types';
import DropdownList from 'react-widgets/DropdownList';
import DatePicker from '../../DataComponent/DataComponent__DatePicker';
import moment from 'moment';
import config from '../../../config';
import TooltipNew from '../../TooltipNew';

export const StatisticsByCountryFilter = ({ startDate, endDate, bindActions, groupBy, count, onAction, t }) => {
    const normalizeCount = useCallback((value) => {
        return (value === undefined || value === null || value === '') ? value : Number(value);
    }, []);
    const [state, setState] = useState({ startDate, endDate, bindActions, groupBy, count: normalizeCount(count) });
    const prevStateRef = useRef(null);

    const hasChanges = useCallback((prev, next) => {
        return ['startDate', 'endDate', 'bindActions', 'groupBy', 'count'].some((key) => {
            const a = prev[key];
            const b = next[key];
            if (key === 'startDate' || key === 'endDate') {
                const av = a && a.valueOf ? a.valueOf() : a;
                const bv = b && b.valueOf ? b.valueOf() : b;
                return av !== bv;
            }
            return a !== b;
        });
    }, []);

    useEffect(() => {
        const next = { startDate, endDate, bindActions, groupBy, count: normalizeCount(count) };
        if (hasChanges(state, next)) {
            setState(next);
        }
    }, [startDate, endDate, bindActions, groupBy, count, hasChanges, normalizeCount]);

    const onGroupParamChange = useCallback((value) => {
        setState(prev => ({ ...prev, groupBy: value.value }));
    }, []);

    const onCountParamChange = useCallback((value) => {
        setState(prev => ({ ...prev, count: normalizeCount(value.value) }));
    }, [normalizeCount]);

    const onBindActionsChange = useCallback((item) => {
        setState(prev => ({ ...prev, bindActions: item.value }));
    }, []);

    const onDataPickerApply = useCallback((datapicker) => {
        const normalizedStart = datapicker.startDate ? moment(datapicker.startDate).startOf('day') : null;
        const normalizedEnd = datapicker.endDate ? moment(datapicker.endDate).endOf('day') : null;

        setState(prev => ({ ...prev, startDate: normalizedStart, endDate: normalizedEnd }));
    }, []);

    useEffect(() => {
        if (!onAction) {
            return;
        }
        const prev = prevStateRef.current;
        if (!prev || hasChanges(prev, state)) {
            prevStateRef.current = state;
            onAction(state);
        }
    }, [state, onAction, hasChanges]);

    const staticNames = (t && t.reportPage) || {};
    const groupParams = useMemo(() => {
        const params = (t && t.statisticsByCountry && t.statisticsByCountry.groupParams) || [];
        return Object.keys(params).map((key) => ({ title: params[key], value: key }));
    }, [t]);
    const countParams = useMemo(() => {
        const params = Array.isArray(config.listCounts) && config.listCounts.length ? config.listCounts : [10,20,50,100];
        return params.map((count) => ({ title: count, value: count }));
    }, []);

    return (
        <div className="statistics__top form-inline form-horizontal">
            <div className="row table-filter">
                <div className="table-filter__item">
                    <label>{staticNames.date}:</label>
                    <DatePicker
                        onApply={onDataPickerApply}
                        startDate={state.startDate}
                        endDate={state.endDate}
                        className="btn form-control reactdaypicker-block"
                    />
                </div>
                <div className="table-filter__item">
                    <label>{t?.timezone?.title} <TooltipNew tooltip={t?.timezone?.timezoneUnchangeableDueAggregation} /> :</label>
                    <DropdownList
                        className={'rw-dropdownlist-select'}
                        filter='startsWith'
                        valueField='value'
                        textField='title'
                        value={'UTC'}
                        defaultValue={'UTC'}
                        data={[{ value: 'UTC', title: 'UTC' }]}
                        disabled={true}
                    />
                </div>
                <div className="table-filter__item">
                    <label>{staticNames.groupBy}:</label>
                    <DropdownList
                        className={'rw-dropdownlist-select'}
                        filter='startsWith'
                        dataKey="value"
                        textField='title'
                        value={state.groupBy}
                        data={groupParams}
                        onChange={onGroupParamChange}
                    />
                </div>
                <div className="table-filter__item">
                    <label>{staticNames.count}:</label>
                    <DropdownList
                        className={'rw-dropdownlist-select'}
                        filter='startsWith'
                        valueField="value"
                        textField='title'
                        value={state.count}
                        data={countParams}
                        onChange={onCountParamChange}
                    />
                </div>
            </div>
        </div>
    );
};

StatisticsByCountryFilter.propTypes = {
    t: PropTypes.object,
    startDate: PropTypes.any,
    endDate: PropTypes.any,
    bindActions: PropTypes.string,
    groupBy: PropTypes.string,
    count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onAction: PropTypes.func
};

export default withTranslation(StatisticsByCountryFilter);
